"use client";

import { Loader2, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { generateDocumentation } from "@/actions/generate-documentation";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DOCUMENTATION_TEMPLATES,
  generateDocumentationButtonLabel,
  resolveFeatureDocumentationGenerateMode,
  type DocumentationTemplateKind,
} from "@/lib/documentation/generate-documentation";

export interface GenerateDocumentationControlsProps {
  readonly targetKind: "facet" | "work";
  readonly targetId: string;
  readonly templateKind: DocumentationTemplateKind | null;
  readonly name: string;
  readonly description: string;
  readonly documentation: string;
  readonly disabled?: boolean;
  /** Overrides the default template-based button label. */
  readonly buttonLabel?: string;
  /**
   * Called with the generated markdown. May persist (e.g. facet update).
   * When it resolves, the flyout already shows the new docs.
   */
  readonly onApplied: (documentation: string) => void | Promise<void>;
  /** When true, success toast says the docs were saved (caller persisted). */
  readonly savesOnApply?: boolean;
}

export function GenerateDocumentationControls({
  targetKind,
  targetId,
  templateKind,
  name,
  description,
  documentation,
  disabled = false,
  buttonLabel: buttonLabelOverride,
  onApplied,
  savesOnApply = false,
}: GenerateDocumentationControlsProps): JSX.Element | null {
  const [generating, setGenerating] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);

  if (!templateKind || !targetId.trim()) return null;

  const hasExisting = Boolean(documentation.trim());
  const featureMode =
    templateKind === "Feature"
      ? resolveFeatureDocumentationGenerateMode(documentation)
      : null;
  const buttonLabel =
    buttonLabelOverride ??
    generateDocumentationButtonLabel(templateKind, hasExisting, documentation);
  const resultLabel =
    featureMode === "executionBriefOnly"
      ? "Execution brief"
      : DOCUMENTATION_TEMPLATES[templateKind].label;

  const handleGenerate = async (): Promise<void> => {
    setGenerating(true);
    try {
      const result = await generateDocumentation({
        targetKind,
        targetId,
        templateKind,
        name,
        description,
        documentation,
        instructions: hasExisting
          ? instructions.trim() || undefined
          : undefined,
      });
      if (!result.success || !result.documentation) {
        toast.error(result.message);
        return;
      }
      await onApplied(result.documentation.trim());
      toast.success(
        savesOnApply
          ? `${resultLabel} saved.`
          : `${resultLabel} added. Save to keep it.`,
      );
    } catch {
      toast.error(
        savesOnApply
          ? `Failed to save ${resultLabel}.`
          : `Failed to generate ${resultLabel}.`,
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 w-full gap-1.5 px-2 text-xs [&_svg]:size-3.5"
        disabled={disabled || generating}
        onClick={() => void handleGenerate()}
      >
        {generating ? (
          <Loader2 className="animate-spin" />
        ) : (
          <Sparkles />
        )}
        {buttonLabel}
      </Button>

      {hasExisting ? (
        showInstructions ? (
          <div className="space-y-2">
            <Label htmlFor={`doc-regen-instructions-${targetId}`}>
              Optional instructions
            </Label>
            <Textarea
              id={`doc-regen-instructions-${targetId}`}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              rows={2}
              disabled={disabled || generating}
              placeholder="e.g. Emphasize compliance risks; keep the FAQ shorter"
            />
          </div>
        ) : (
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            onClick={() => setShowInstructions(true)}
            disabled={disabled || generating}
          >
            Add optional instructions
          </button>
        )
      ) : null}
    </div>
  );
}
